import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Header, Footer } from "../components/Chrome";

const VALUES = [
  { n: "01", t: "Competitive Pricing", b: "Live wholesale pricing without markup theatre. Volume rewards that scale with your business." },
  { n: "02", t: "Reliable Supply", b: "Trusted brands in stock, plus a curated shelf of unique imports your competitors won't have." },
  { n: "03", t: "Responsive Service", b: "Real people on the phone, same-day quotes, and answers before the next truck rolls." },
  { n: "04", t: "Convenient Ordering", b: "Approved retailers order from the portal in minutes. Delivery scheduled or curbside pickup." },
];

const CUSTOMERS = [
  "Convenience Stores",
  "Gas Stations",
  "Hospital Gift Shops",
  "Independent Retailers",
  "Cafeterias",
  "Grab-and-Go Outlets",
];

export default function About() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />

      {/* Hero */}
      <section ref={heroRef} className="relative pt-32 pb-24 md:pb-32 overflow-hidden" data-testid="about-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0 -z-0">
          <img
            src="https://images.pexels.com/photos/4483609/pexels-photo-4483609.jpeg?w=1920"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-paper/60" />
        </motion.div>
        <div className="relative px-6 md:px-12 lg:px-16">
          <div className="overline text-black/50 mb-6">About</div>
          <h1 className="font-display text-[14vw] md:text-[10vw] lg:text-[9vw] uppercase leading-[0.85] tracking-tighter max-w-6xl">
            Built for the<br/>
            <em className="font-editorial italic tracking-normal">independents.</em>
          </h1>
          <p className="mt-10 max-w-2xl text-lg md:text-xl leading-relaxed">
            GTA Wholesale Supply is a Canadian wholesale distribution company serving convenience stores, gas stations, independent retailers, hospital gift shops, cafeterias, and grab-and-go outlets across the Greater Toronto Area and surrounding Ontario.
          </p>
        </div>
      </section>

      {/* Manifesto */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10" data-testid="about-manifesto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-black/50 mb-6">Our mission</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tight">
              Retailers<br/>who stand out<br/><em className="font-editorial italic tracking-normal">win.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg leading-relaxed space-y-6">
            <p>
              We supply the trusted brands your customers already ask for — beverages, chips, chocolates, candies, protein bars, energy drinks, and the everyday essentials that keep the impulse aisle moving.
            </p>
            <p>
              And then we go further. We import unique international products — including traditional Indian Banta (Codd-Neck) beverages — that give retailers access to exciting SKUs no one else in the neighbourhood carries. That's what turns a corner store into a destination.
            </p>
            <p className="font-editorial italic text-2xl leading-snug">
              "Competitive wholesale pricing, dependable delivery, and exceptional customer service — for the independent retailers who make this city move."
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-ink text-paper px-6 md:px-12 lg:px-16 py-24 md:py-32" data-testid="about-values">
        <div className="overline text-white/50 mb-6">Why choose us</div>
        <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tighter mb-16">
          Four commitments.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {VALUES.map((v, i) => (
            <motion.div key={v.n}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16,1,0.3,1] }}
              className="border border-white/20 p-8 md:p-10 hover:border-soda transition-colors duration-300"
              data-testid={`about-value-${v.n}`}
            >
              <div className="font-display text-7xl md:text-8xl leading-none text-soda mb-6">{v.n}</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mb-4">{v.t}</h3>
              <p className="text-white/70 leading-relaxed">{v.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Who we serve */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10" data-testid="about-customers">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="overline text-black/50 mb-6">Who we serve</div>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter">Six ways to stock a shelf.</h2>
          </div>
          <ul className="lg:col-span-7 lg:col-start-6 space-y-3">
            {CUSTOMERS.map((c, i) => (
              <motion.li key={c}
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="border-b border-ink pb-3 flex items-baseline gap-6"
              >
                <span className="overline text-black/40 w-10">0{i+1}</span>
                <span className="font-display text-3xl md:text-5xl uppercase leading-none tracking-tight">{c}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Service Area */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10" data-testid="about-service-area">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="overline text-black/50 mb-6">Service Area</div>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter mb-8">
              GTA<br/>+ surrounding<br/>Ontario.
            </h2>
            <p className="text-lg leading-relaxed max-w-md">
              Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Oakville, Burlington, Hamilton, Ajax, Pickering, Whitby, Oshawa — plus surrounding Ontario on request.
            </p>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="border border-ink p-8 hard-shadow bg-paper mb-6">
              <div className="overline text-black/50 mb-2">Delivery</div>
              <div className="font-display text-3xl leading-none mb-3">FREE OVER $500</div>
              <p className="text-sm text-black/70 leading-relaxed">Same-week delivery across the GTA. Flat-rate delivery below the threshold.</p>
            </div>
            <div className="border border-ink p-8 bg-paper">
              <div className="overline text-black/50 mb-2">Pickup</div>
              <div className="font-display text-3xl leading-none mb-3">SCARBOROUGH · ON</div>
              <p className="text-sm text-black/70 leading-relaxed">Curbside pickup at our warehouse — Mon–Fri, 8am–5pm ET.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-paper border-t border-ink" data-testid="about-cta">
        <div className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="overline mb-6">Ready?</div>
          <Link to="/apply" className="block group" data-testid="about-apply-link">
            <div className="font-display text-[18vw] md:text-[13vw] uppercase leading-[0.85] tracking-tighter transition-colors group-hover:text-cobalt">
              Apply for<br/>wholesale →
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
